
import React, { useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Thermometer, Fan, Droplets, Power } from 'lucide-react';

interface HvacControlPanelProps {
  selectedFloor: number;
  selectedZone: string | null;
}

const HvacControlPanel: React.FC<HvacControlPanelProps> = ({ selectedFloor, selectedZone }) => {
  const [coolingTemp, setCoolingTemp] = useState(22);
  const [heatingTemp, setHeatingTemp] = useState(18);
  const [fanSpeed, setFanSpeed] = useState(50);
  const [isHvacOn, setIsHvacOn] = useState(true);
  
  const getZoneName = () => {
    if (!selectedZone) return "No Zone Selected";
    
    const floorPrefix = selectedZone.split('-')[0];
    const zoneName = selectedZone.split('-')[1];
    
    if (floorPrefix === 'gf') return `Ground Floor - ${zoneName}`;
    if (floorPrefix === 'ff') return `First Floor - ${zoneName}`;
    if (floorPrefix === 'sf') return `Second Floor - ${zoneName}`;
    
    return selectedZone;
  };

  return (
    <div className="p-4 h-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center">
          <Thermometer className="mr-2 h-5 w-5" />
          HVAC Control Panel
        </h2>
        <div className="flex items-center space-x-2">
          <Label htmlFor="hvac-power" className="text-sm">Power</Label>
          <Switch 
            id="hvac-power" 
            checked={isHvacOn} 
            onCheckedChange={setIsHvacOn} 
          />
        </div>
      </div>
      
      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md mb-4">
        <p className="text-sm font-medium">{getZoneName()}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {selectedZone ? "Adjust settings for this zone" : "Select a zone from the floor map"}
        </p>
      </div>
      
      <div className={`space-y-6 ${!isHvacOn && 'opacity-50 pointer-events-none'}`}>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm flex items-center">
              <Thermometer className="mr-1 h-4 w-4 text-blue-500" />
              Cooling Setpoint
            </Label>
            <span className="text-sm font-medium">{coolingTemp}°C</span>
          </div>
          <Slider 
            value={[coolingTemp]} 
            min={16} 
            max={28} 
            step={0.5}
            onValueChange={(values) => setCoolingTemp(values[0])}
            className="max-w-full"
            disabled={!selectedZone || !isHvacOn}
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm flex items-center">
              <Thermometer className="mr-1 h-4 w-4 text-orange-500" />
              Heating Setpoint
            </Label>
            <span className="text-sm font-medium">{heatingTemp}°C</span>
          </div>
          <Slider 
            value={[heatingTemp]} 
            min={15} 
            max={24} 
            step={0.5}
            onValueChange={(values) => setHeatingTemp(values[0])}
            className="max-w-full"
            disabled={!selectedZone || !isHvacOn}
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm flex items-center">
              <Fan className="mr-1 h-4 w-4 text-gray-500" />
              Fan Speed
            </Label>
            <span className="text-sm font-medium">{fanSpeed}%</span>
          </div>
          <Slider 
            value={[fanSpeed]} 
            min={0} 
            max={100} 
            step={5}
            onValueChange={(values) => setFanSpeed(values[0])}
            className="max-w-full"
            disabled={!selectedZone || !isHvacOn}
          />
        </div>
        
        <div className="flex justify-between items-center pt-2">
          <div className="flex items-center space-x-2">
            <Label htmlFor="auto-mode" className="text-sm">Auto Mode</Label>
            <Switch id="auto-mode" disabled={!selectedZone || !isHvacOn} />
          </div>
          
          <div className="flex items-center space-x-2">
            <Label htmlFor="eco-mode" className="text-sm">Eco Mode</Label>
            <Switch id="eco-mode" disabled={!selectedZone || !isHvacOn} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HvacControlPanel;
